/**
 * S2P Effectiveness Dashboard — Phase 2.B Step 8.C
 *
 * KPI page that consumes GET /api/pece/s2p/dashboard?window_days=N.
 * Shows the feedback loop of the Autonomous Procurement Agent:
 *   • Trust Score (single number — main KPI)
 *   • Tier Distribution (auto / draft / suggest / log / blocked)
 *   • Top Blockers (operational improvement signals)
 *   • Lead Time metrics (decision → PPR, PPR → reservation)
 *
 * Foundation for Sprint 5 Self-Tuning: trust_score → control signal.
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Brain, Gauge, BarChart3, AlertTriangle, Clock, RefreshCw,
  Loader2, TrendingUp, ListChecks, Sparkles,
} from 'lucide-react';

const TIER_META = {
  auto_execute:     { label: 'Auto Executed',  emoji: '🟢', cls: 'bg-emerald-500',  text: 'text-emerald-700' },
  draft_for_review: { label: 'Draft — Review', emoji: '🟡', cls: 'bg-amber-500',    text: 'text-amber-700'   },
  suggest_only:     { label: 'Suggestion',     emoji: '🟠', cls: 'bg-orange-500',   text: 'text-orange-700'  },
  log_only:         { label: 'Log Only',       emoji: '🔵', cls: 'bg-sky-500',      text: 'text-sky-700'     },
  blocked:          { label: 'Blocked',        emoji: '🔴', cls: 'bg-rose-500',     text: 'text-rose-700'    },
};

const fmtSec = (s) => {
  if (s == null) return '—';
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) return `${(s / 60).toFixed(1)} دقيقة`;
  return `${(s / 3600).toFixed(1)} ساعة`;
};

const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const S2PEffectivenessDashboard = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/pece/s2p/dashboard?window_days=${windowDays}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تحميل لوحة الفعالية');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowDays]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="s2p-effectiveness-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider mb-1 flex items-center gap-2">
              <Brain className="w-4 h-4" /> S2P · Effectiveness Dashboard
            </div>
            <h1 className="text-2xl font-bold text-gray-900">لوحة فعالية الذكاء التلقائي</h1>
            <p className="text-sm text-gray-500 mt-1">
              يقيس النظام نفسه — Trust Score، توزيع القرارات، أكثر العوائق شيوعاً، وزمن التنفيذ.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="s2p-dashboard-window"
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value={7}>آخر 7 أيام</option>
              <option value={30}>آخر 30 يوم</option>
              <option value={90}>آخر 90 يوم</option>
              <option value={180}>آخر 180 يوم</option>
            </select>
            <button
              data-testid="s2p-dashboard-refresh"
              onClick={load}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> تحديث
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-gray-500" data-testid="s2p-dashboard-loading">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري التحميل...
          </div>
        )}

        {!loading && data && (
          <div className="space-y-6">
            <KPITotalsRow totals={data.totals} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TrustScoreCard score={data.trust_score} rates={data.rates} />
              <TierDistributionCard distribution={data.distribution} total={data.totals.decisions} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LeadTimeCard leadTime={data.lead_time} totals={data.totals} />
              <TopBlockersCard blockers={data.blockers} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── KPI Totals Row ───────────────────────────────────────────────────────

const KPITotalsRow = ({ totals }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="s2p-totals-row">
    <KPI icon={<ListChecks className="w-5 h-5" />} label="عمليات المسح" value={totals.runs} />
    <KPI icon={<BarChart3 className="w-5 h-5" />} label="إجمالي القرارات" value={totals.decisions} />
    <KPI icon={<Sparkles className="w-5 h-5" />} label="PPR تلقائية" value={totals.auto_pprs} tone="emerald" />
    <KPI icon={<TrendingUp className="w-5 h-5" />} label="PPR مُكتملة" value={totals.fulfilled_pprs} tone="indigo" />
  </div>
);

const KPI = ({ icon, label, value, tone = 'gray' }) => {
  const toneCls = {
    gray: 'text-gray-600 bg-gray-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    indigo: 'text-indigo-700 bg-indigo-50',
  }[tone];
  return (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg ${toneCls}`}>{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
};

// ─── Trust Score (Main KPI) ───────────────────────────────────────────────

const TrustScoreCard = ({ score, rates }) => {
  // Gauge: 0..1 → 0..100 → label color
  const pct = Math.round((score || 0) * 100);
  const tone =
    pct >= 80 ? { ring: 'stroke-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50', label: 'ممتاز' } :
    pct >= 60 ? { ring: 'stroke-amber-500', text: 'text-amber-600', bg: 'bg-amber-50', label: 'مقبول' } :
    pct >= 40 ? { ring: 'stroke-orange-500', text: 'text-orange-600', bg: 'bg-orange-50', label: 'تحتاج تحسين' } :
                 { ring: 'stroke-rose-500', text: 'text-rose-600', bg: 'bg-rose-50', label: 'منخفض' };
  const C = 2 * Math.PI * 80;
  const offset = C - (pct / 100) * C;

  return (
    <div className="bg-white rounded-xl border p-6" data-testid="trust-score-card">
      <div className="flex items-center gap-2 mb-4">
        <Gauge className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-bold">Trust Score</h3>
        <span className="text-xs text-gray-500 ml-auto">control signal for Sprint 5 Self-Tuning</span>
      </div>
      <div className="flex items-center gap-6">
        <div className="relative w-48 h-48 shrink-0">
          <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
            <circle cx="100" cy="100" r="80" strokeWidth="14" stroke="#e5e7eb" fill="none" />
            <circle
              cx="100" cy="100" r="80" strokeWidth="14" fill="none"
              strokeLinecap="round" className={tone.ring}
              strokeDasharray={C} strokeDashoffset={offset}
              style={{ transition: 'stroke-dashoffset 0.6s' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={`text-5xl font-bold ${tone.text}`} data-testid="trust-score-value">{pct}</div>
            <div className={`text-xs uppercase font-bold tracking-wider mt-1 px-2 py-0.5 rounded ${tone.bg} ${tone.text}`}>
              {tone.label}
            </div>
          </div>
        </div>
        <div className="flex-1 space-y-2 text-sm">
          <RateRow label="Auto Execute Rate" value={rates.auto_execute_rate} good />
          <RateRow label="Fulfilment Rate" value={rates.fulfilment_rate} good />
          <RateRow label="Blocked Rate" value={rates.blocked_rate} bad />
          <div className="text-[11px] text-gray-500 mt-3 leading-relaxed border-t pt-2">
            <code>trust = auto_rate + fulfilment_rate − blocked_rate</code>
            <br />مُحدّد بين 0 و 1.
          </div>
        </div>
      </div>
    </div>
  );
};

const RateRow = ({ label, value, good, bad }) => {
  const pct = Math.round((value || 0) * 100);
  const cls = good ? 'bg-emerald-500' : bad ? 'bg-rose-500' : 'bg-gray-400';
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-bold">{pct}%</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full ${cls}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// ─── Tier Distribution ────────────────────────────────────────────────────

const TierDistributionCard = ({ distribution, total }) => {
  const entries = Object.entries(distribution || {});
  return (
    <div className="bg-white rounded-xl border p-6" data-testid="tier-distribution-card">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-indigo-600" />
        <h3 className="text-lg font-bold">Tier Distribution</h3>
        <span className="text-xs text-gray-500 ml-auto">إجمالي: {total} قرار</span>
      </div>

      {/* Stacked bar */}
      <div className="h-6 bg-gray-100 rounded-full overflow-hidden flex mb-4">
        {entries.map(([tier, v]) => {
          if (!v.pct) return null;
          const m = TIER_META[tier] || TIER_META.log_only;
          return (
            <div
              key={tier}
              data-testid={`tier-bar-${tier}`}
              className={m.cls}
              style={{ width: `${v.pct}%` }}
              title={`${m.label}: ${v.count} (${v.pct}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {entries.map(([tier, v]) => {
          const m = TIER_META[tier] || TIER_META.log_only;
          return (
            <div
              key={tier}
              data-testid={`tier-row-${tier}`}
              className="flex items-center gap-3"
            >
              <span className="text-xl">{m.emoji}</span>
              <span className="text-sm flex-1">{m.label}</span>
              <span className={`text-sm font-bold ${m.text}`}>{v.count}</span>
              <span className="text-xs text-gray-500 w-16 text-left">{fmtPct(v.pct)}</span>
              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full ${m.cls}`} style={{ width: `${v.pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Lead Time ────────────────────────────────────────────────────────────

const LeadTimeCard = ({ leadTime, totals }) => (
  <div className="bg-white rounded-xl border p-6" data-testid="lead-time-card">
    <div className="flex items-center gap-2 mb-4">
      <Clock className="w-5 h-5 text-indigo-600" />
      <h3 className="text-lg font-bold">Lead Time (Funnel)</h3>
      <span className="text-xs text-gray-500 ml-auto">قياس سرعة الذكاء</span>
    </div>
    <div className="space-y-4">
      <FunnelStep
        from="Decision" to="PPR Created"
        value={leadTime.avg_decision_to_ppr_seconds}
        sample={totals.auto_pprs}
      />
      <div className="border-l-2 border-dashed border-gray-300 ml-4 h-2"></div>
      <FunnelStep
        from="PPR Created" to="Stock Reserved"
        value={leadTime.avg_ppr_to_reservation_seconds}
        sample={totals.auto_pprs}
      />
    </div>
    {(!leadTime.avg_decision_to_ppr_seconds && !leadTime.avg_ppr_to_reservation_seconds) && (
      <div className="mt-4 p-3 bg-gray-50 rounded text-xs text-gray-500 text-center">
        لا توجد بيانات lead time بعد. شغّل المسح بقرارات tier auto_execute لإنتاج بيانات قياس.
      </div>
    )}
  </div>
);

const FunnelStep = ({ from, to, value, sample }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1 text-xs">
      <div className="font-bold text-gray-700">{from} → {to}</div>
      <div className="text-gray-500">عينة: {sample || 0}</div>
    </div>
    <div className="text-right">
      <div className="text-2xl font-bold text-indigo-700">{fmtSec(value)}</div>
      <div className="text-[10px] text-gray-500 uppercase tracking-wider">avg</div>
    </div>
  </div>
);

// ─── Top Blockers ─────────────────────────────────────────────────────────

const BLOCKER_LABELS = {
  missing_schedule_task: 'لا يوجد نشاط في الجدول',
  schedule_task_missing_baseline: 'النشاط بدون baseline',
  missing_resource_plan: 'لا توجد خطة موارد',
  procurement_method_not_purchase: 'طريقة الشراء غير مدعومة',
  active_ppr_conflict: 'يوجد PPR نشط بالفعل',
  no_material_gap_at_execution_time: 'لا توجد فجوة مواد للتنفيذ',
};

const TopBlockersCard = ({ blockers }) => {
  const maxCount = Math.max(1, ...blockers.map((b) => b.count));
  return (
    <div className="bg-white rounded-xl border p-6" data-testid="top-blockers-card">
      <div className="flex items-center gap-2 mb-4">
        <AlertTriangle className="w-5 h-5 text-rose-500" />
        <h3 className="text-lg font-bold">أكثر العوائق شيوعاً</h3>
        <span className="text-xs text-gray-500 ml-auto">Policy Guard improvement signals</span>
      </div>
      {blockers.length === 0 ? (
        <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
          ✨ لا توجد عوائق — Policy Guard سليم
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="blockers-list">
          {blockers.map((b) => (
            <div key={b.code} data-testid={`blocker-${b.code}`} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-700">
                  {BLOCKER_LABELS[b.code] || b.code}
                </div>
                <div className="text-[10px] text-gray-400 font-mono">{b.code}</div>
              </div>
              <div className="w-1/2 flex items-center gap-2">
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-rose-500"
                    style={{ width: `${(b.count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold w-12 text-left">{b.count}</span>
                <span className="text-[10px] text-gray-400 w-12 text-left">{fmtPct(b.pct)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default S2PEffectivenessDashboard;
